import React from "react";
import Image from "next/image";
import Link from "next/link";
import { GetStaticProps } from "next";
// components
import CustomHead from "src/components/ui/CustomHead";
import Layout from "src/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import useAllPage from "src/hooks/dynamic/allPage";
// constants
import { EVariants } from "src/constants/variants";
import { Globe, Building, Users, CheckCircle, ArrowRight, Linkedin } from "lucide-react";

// All partners combined - logos and people
const allPartners = [
  {
    name: "Inside Minds",
    logo: "/images/partners/inside-minds-updated.png",
    website: "https://www.inside-minds.com/",
    type: "partner",
  },
  {
    name: "VetLed",
    logo: "/images/partners/vetled-updated.jpeg",
    website: "https://www.vetled.co.uk",
    type: "partner",
  },
  {
    name: "Practice Made Purrfect",
    logo: "/images/partners/practice-made-purrfect-updated.png",
    website: "https://www.practicemadepurrfect.com/",
    type: "partner",
  },
  {
    name: "VetLife",
    logo: "/images/partners/vetlife-updated.png",
    website: "https://www.vetlife.org.uk/",
    type: "partner",
  },
  {
    name: "SPVS",
    logo: "/images/partners/spvs-updated.png",
    website: "https://spvs.org.uk/",
    type: "partner",
  },
  {
    name: "Covetrus",
    logo: "/images/partners/covetrus-updated.png",
    website: "https://www.covetrus.co.uk/",
    type: "partner",
  },
  {
    name: "ClientTrax",
    logo: "/images/partners/clientrax-updated.jpeg",
    website: "https://www.clientrax.com/",
    type: "partner",
  },
  {
    name: "IDEXX Animana",
    logo: "/images/partners/idexx-animana.webp",
    website: "https://www.idexx.co.uk/en-gb/veterinary/software-services/animana/",
    type: "partner",
    logoWidth: 200,
    logoHeight: 53,
    logoClassName: "bg-gray-900 rounded-md p-3",
  },
  {
    name: "The Vet Station",
    logo: "/images/partners/the-vet-station-updated.jpeg",
    website: "https://www.thevetstationmolesey.co.uk/",
    type: "partner",
  },
  {
    name: "NVA",
    logo: "/images/partners/nva-updated.png",
    website: "https://www.nva.com/",
    type: "partner",
  },
  {
    name: "NVS",
    logo: "/images/partners/nvs-updated.jpeg",
    website: "https://www.nvsweb.co.uk/",
    type: "partner",
  },
  {
    name: "MWI Animal Health",
    logo: "/images/partners/mwi.webp",
    website: "https://www.mwiah.co.uk/",
    type: "partner",
    logoWidth: 176,
    logoHeight: 28,
    logoClassName: "bg-gray-900 rounded-md p-3",
  },
  {
    name: "Veterinary IT Services",
    logo: "/images/partners/veterinaryit-services-updated.png",
    website: "https://veterinaryit.services/",
    type: "partner",
  },
  {
    name: "VetTimes",
    logo: "/images/partners/vettimes-updated.png",
    website: "https://www.vettimes.co.uk/",
    type: "partner",
  },
  {
    name: "RxWorks",
    logo: "/images/partners/rxworks-updated.png",
    website: "https://software.covetrus.com/emea/veterinary-solutions/rxworks-veterinary-inventory-management/",
    type: "partner",
  },
  {
    name: "Pack Leader DVM",
    logo: "/images/partners/pack-leader-dvm.webp",
    website: "https://www.packleaderdvm.com/",
    type: "partner",
  },
  {
    name: "VetPartners",
    logo: "/images/partners/vetpartners.png",
    website: "https://vetpartners.org/",
    type: "partner",
  },
  {
    name: "VETcpd",
    logo: "/images/partners/vetcpd.png",
    website: "https://vetcpd.co.uk",
    type: "partner",
  },
  {
    name: "Veterinary Leadership Academy",
    role: "Leadership Development",
    image: "/images/people/veterinary-leadership-academy-updated.png",
    link: "https://www.linkedin.com/company/veterinary-leadership-academy/",
    type: "person",
  },
  {
    name: "VetYogi",
    role: "Wellness Instructor",
    image: "/images/people/vet-yogi-updated.jpeg",
    link: "https://www.linkedin.com/in/vetyogi/",
    type: "person",
  },
  {
    name: "Dr. Pete Weinstein",
    role: "Veterinary Consultant",
    image: "/images/people/dr-pete-weinstein-updated.png",
    link: "https://www.linkedin.com/in/pawdr",
    type: "person",
  },
  {
    name: "Dr. Ken",
    role: "Pet Health Specialist",
    image: "/images/people/dr-ken-updated.png",
    link: "https://www.linkedin.com/in/kenlambrecht/",
    type: "person",
  },
  {
    name: "Dr Donita",
    role: "Animal Care Specialist",
    image: "/images/people/dr-donita-updated.png",
    link: "https://www.linkedin.com/in/askdrdonita/",
    type: "person",
  },
];

export default function PartnersPage() {
  return (
    <>
      <CustomHead
        title="Partners | PetsApp"
        description="Discover PetsApp's network of technology, educational, and referral partners helping veterinary practices succeed."
      />
      <Layout headerProps={{ variant: EVariants.PRIMARY }}>
        <div className="min-h-screen">
          {/* Hero Section */}
          <section className="bg-primary py-16 md:py-24 relative overflow-hidden">
            {/* Top fade gradient */}
            <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-[#4285F4] to-primary pointer-events-none z-0"></div>
            <div className="container mx-auto px-4 md:px-6 relative z-10">
              <div className="max-w-3xl mx-auto text-center">
                <h1 className="text-3xl font-bold sm:text-4xl md:text-5xl lg:text-6xl text-white mb-6 font-cooper">
                  PetsApp Partners
                </h1>
                <div className="flex justify-center">
                  <a href="https://www.facebook.com/groups/2479024119027825/" target="_blank" rel="noopener noreferrer">
                    <Button className="bg-accent text-white hover:bg-accent/90 font-bold rounded-full shadow-[0_4px_14px_0_rgba(255,255,255,0.5)]">
                      Let's PetsApp Together
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* All Partners Section - Combined logos and people in one grid */}
          <section className="py-16 md:py-24 bg-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {allPartners.map((partner, index) => (
                  <a
                    key={index}
                    href={partner.type === "partner" ? partner.website : partner.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group"
                  >
                    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all transform group-hover:-translate-y-1 overflow-hidden text-center p-6 flex flex-col items-center justify-center h-[240px]">
                      {partner.type === "partner" ? (
                        // Partner logo layout
                        <>
                          <div className="flex-1 flex items-center justify-center mb-4">
                            <div className={partner.logoClassName}>
                              <Image
                                src={partner.logo || "/placeholder.svg"}
                                alt={partner.name}
                                width={partner.logoWidth || 160}
                                height={partner.logoHeight || 160}
                                className="max-h-[140px] max-w-[200px] object-contain"
                              />
                            </div>
                          </div>
                          <div className="text-center">
                            <h3 className="font-cooper text-lg font-bold text-gray-900">{partner.name}</h3>
                          </div>
                        </>
                      ) : (
                        // Person profile layout
                        <>
                          <div className="relative w-32 h-32 mx-auto mb-3 rounded-full overflow-hidden">
                            <Image
                              src={partner.image || "/placeholder.svg"}
                              alt={partner.name}
                              width={128}
                              height={128}
                              className="object-cover w-full h-full"
                            />
                          </div>
                          <h3 className="font-cooper text-lg font-bold text-gray-900">{partner.name}</h3>
                          <p className="text-gray-600 text-sm font-brown">{partner.role}</p>
                          {partner.link &&
                            partner.link.includes("linkedin") &&
                            partner.name !== "Veterinary Leadership Academy" &&
                            partner.name !== "VetYogi" && (
                              <div className="mt-2 flex justify-center">
                                <Linkedin className="h-5 w-5 text-primary" />
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </section>

          {/* Partner Types Section */}
          <section className="py-16 md:py-24 bg-primary">
            <div className="container mx-auto px-4 md:px-6">
              <div className="text-center max-w-3xl mx-auto mb-12">
                <h2 className="text-3xl font-cooper text-white font-bold sm:text-4xl">Partner with PetsApp</h2>
                <p className="mt-4 text-white/90 md:text-lg font-brown">
                  Explore the different ways to collaborate and grow with us
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {[
                  {
                    icon: <Globe className="h-10 w-10 text-white" />,
                    title: "Technology Partners",
                    description:
                      "Join us in a tech partnership and collaborate on creating seamless integrations that enhance the veterinary practice experience.",
                    benefits: ["API access and documentation", "Co-marketing opportunities", "Joint product development"],
                  },
                  {
                    icon: <Building className="h-10 w-10 text-white" />,
                    title: "Educational Partners",
                    description:
                      "Join our educational network and collaborate on innovative content beneficial to our community.",
                    benefits: [
                      "Create joint educational content",
                      "Access shared learning resources",
                      "Offer exclusive member discounts",
                    ],
                  },
                  {
                    icon: <Users className="h-10 w-10 text-white" />,
                    title: "Referral Partners",
                    description:
                      "Join our referral program and help connect practices with PetsApp while earning valuable rewards for your network.",
                    benefits: [
                      "FREE iPad for referred clinics",
                      "Full FREE access to our Academy",
                      "FREE PetsApp Together tickets",
                    ],
                  },
                ].map((type, i) => (
                  <Card
                    key={i}
                    className="border-none shadow-md hover:shadow-lg transition-shadow bg-tertiary flex flex-col"
                  >
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="mb-4">{type.icon}</div>
                      <h3 className="text-xl font-cooper font-bold mb-2 text-white">{type.title}</h3>
                      <p className="text-white/90 mb-4 font-brown">{type.description}</p>
                      <ul className="space-y-2 mb-auto">
                        {type.benefits.map((benefit, j) => (
                          <li key={j} className="flex items-start gap-2">
                            <CheckCircle className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
                            <span className="font-brown text-white/90">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-6 pt-4">
                        <Link href="https://petsapp.com/demo">
                          <Button className="bg-accent text-white hover:bg-accent/90 font-bold rounded-full">
                            Learn more
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Success Stories Section */}
          <section className="py-16 md:py-20 bg-primary mt-0">
            <div className="container mx-auto px-4 md:px-6">
              <div className="text-center max-w-3xl mx-auto mb-12">
                <h2 className="text-3xl font-cooper text-white font-bold sm:text-4xl">Partner Success Stories</h2>
                <p className="mt-4 text-white/90 md:text-lg font-brown">
                  See how our partners have achieved success with PetsApp
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <Card className="border-none shadow-md overflow-hidden flex flex-col bg-white">
                  <a href="https://meeting.petsapp.com/spvs-partners" target="_blank" rel="noopener noreferrer" className="block w-full">
                    <div className="aspect-video relative w-full overflow-hidden">
                      <Image
                        src="/images/partners/spvs-petsapp-partnership.png"
                        alt="SPVS and PetsApp Partnership"
                        width={800}
                        height={450}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </a>
                  <CardContent className="p-6 flex flex-col flex-grow">
                    <div className="flex-grow" style={{ minHeight: "180px" }}>
                      <h3 className="text-xl font-cooper text-subheading font-bold mb-2 text-gray-900">
                        How SPVS and PetsApp Transformed Continuing Education
                      </h3>
                      <p className="text-gray-600 font-brown text-paragraph">
                        This community collaboration has brought together some of Pet Health's most leading KOLs for
                        your continued learning.
                      </p>
                    </div>
                    <div className="mt-4">
                      <a href="https://meeting.petsapp.com/spvs-partners" target="_blank" rel="noopener noreferrer">
                        <Button className="bg-accent text-white hover:bg-accent/90 font-bold rounded-full">
                          Learn more <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-none shadow-md overflow-hidden flex flex-col bg-white">
                  <div className="aspect-video relative w-full overflow-hidden">
                    <Image
                      src="/images/partners/home-delivery.png"
                      alt="PetsApp, Covetrus and NVS Collaboration"
                      width={800}
                      height={450}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-6 flex flex-col flex-grow">
                    <div className="flex-grow" style={{ minHeight: "180px" }}>
                      <h3 className="text-xl font-cooper text-subheading font-bold mb-2 text-gray-900">
                        PetsApp Announces Game-Changing Collaboration with Covetrus and NVS
                      </h3>
                      <p className="text-gray-600 font-brown text-paragraph">
                        Discover how this strategic partnership is revolutionizing prescription home delivery for
                        veterinary practices across the UK, enhancing client convenience and practice efficiency.
                      </p>
                    </div>
                    <div className="mt-4">
                      <a
                        href="https://petsapp.com/blog/petsapp-announces-game-changing-collaboration-with-covetrus-and-nvs"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button className="bg-accent text-white hover:bg-accent/90 font-bold rounded-full">
                          Read article <ArrowRight className="h-4 w-4 ml-1" />
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section className="py-16 bg-primary">
            <div className="container mx-auto px-4 md:px-6">
              <div className="bg-tertiary rounded-xl p-8 md:p-12 text-white text-center max-w-4xl mx-auto relative overflow-hidden">
                <div className="absolute right-0 bottom-0 opacity-10 translate-x-1/4 translate-y-1/4">
                  <Image
                    src="https://petsapp.com/_next/image?url=%2Fimages%2FHome%2Fyorkie.png&w=3840&q=75"
                    alt="Yorkie illustration"
                    width={300}
                    height={300}
                    className="w-[300px] h-[300px]"
                  />
                </div>

                <div className="relative z-10">
                  <h2 className="text-2xl md:text-3xl font-cooper font-bold mb-4">
                    Have questions about our partner program?
                  </h2>
                  <p className="text-white/90 mb-6 max-w-2xl mx-auto font-brown">
                    Our partnership team is ready to discuss how we can collaborate to bring value to veterinary
                    practices.
                  </p>
                  <Link href="https://meetings.hubspot.com/naomi-oikonomou/revenue-growth">
                    <Button className="bg-accent text-white hover:bg-accent/90 font-bold rounded-full">
                      Schedule a demo
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </div>
      </Layout>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const initialReduxState = await useAllPage(ctx.locale as string);

  return {
    props: {
      initialReduxState: { DYNAMIC: initialReduxState.DYNAMIC },
    },
    revalidate: 600, // 10 Minutes Cache
  };
};
